import { MoreVertical } from 'lucide-react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/shared/ui/Button/Button';
import { Tooltip } from '@/shared/ui/Tooltip/Tooltip';
import styles from './ActionMenu.module.css';

export type ActionMenuItem = {
  id: string;
  label: string;
  /** Identificador ao lado do rotulo (ex.: #670745). */
  detail?: string;
  /** Faixa de cabecalho, com divisor antes dos demais itens. */
  header?: boolean;
  /** Tooltip do item (ex.: Copiar link). */
  title?: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
};

type ActionMenuProps = {
  items: ActionMenuItem[];
  ariaLabel: string;
  align?: 'left' | 'right';
  /** Alinha visualmente ao CTA da ListToolbar (outline lg). */
  toolbarTrigger?: boolean;
  /** Classe extra no botao trigger. */
  triggerClassName?: string;
  /**
   * Renderiza o menu em portal com position:fixed (evita corte por overflow
   * de pais como bolhas de chat).
   */
  portal?: boolean;
};

export function ActionMenu({
  items,
  ariaLabel,
  align = 'right',
  toolbarTrigger = false,
  triggerClassName,
  portal = false,
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open || !portal) return;
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 180;
    const left =
      align === 'left'
        ? rect.left
        : Math.max(8, rect.right - menuWidth);
    setCoords({ top: rect.bottom + 4, left });
  }, [open, portal, align]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (items.length === 0) {
    if (!toolbarTrigger) return null;
    return (
      <div className={styles.wrap}>
        <Button
          type="button"
          variant="outline"
          size="lg"
          className={styles.toolbarTrigger}
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={false}
          disabled
        >
          <MoreVertical size={18} aria-hidden />
        </Button>
      </div>
    );
  }

  const menu = open ? (
    <div
      ref={menuRef}
      className={`${styles.menu} ${align === 'left' ? styles.menuLeft : ''} ${
        portal ? styles.menuPortal : ''
      }`}
      role="menu"
      style={
        portal && coords
          ? { top: coords.top, left: coords.left, right: 'auto' }
          : undefined
      }
    >
      {items.map((item, index) => {
        const botao = (
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled}
            className={`${item.header ? styles.itemHeader : styles.item} ${
              item.danger ? styles.danger : ''
            } ${item.disabled ? styles.disabled : ''}`}
            aria-label={item.detail ? `${item.detail} - ${item.label}` : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (item.disabled) return;
              setOpen(false);
              item.onClick();
            }}
          >
            {item.detail ? (
              <span className={styles.itemLinha}>
                <span className={styles.detail}>{item.detail}</span>
                <span>{item.label}</span>
              </span>
            ) : (
              item.label
            )}
          </button>
        );
        return (
          <Fragment key={item.id}>
            {item.title ? (
              <Tooltip
                content={item.title}
                placement="top"
                nowrap
                triggerClassName={styles.tipCheio}
              >
                {botao}
              </Tooltip>
            ) : (
              botao
            )}
            {item.header && index < items.length - 1 ? (
              <div className={styles.divider} role="separator" />
            ) : null}
          </Fragment>
        );
      })}
    </div>
  ) : null;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <Button
        ref={triggerRef}
        type="button"
        variant={toolbarTrigger ? 'outline' : 'ghost'}
        size={toolbarTrigger ? 'lg' : 'sm'}
        className={`${toolbarTrigger ? styles.toolbarTrigger : styles.triggerBtn}${
          triggerClassName ? ` ${triggerClassName}` : ''
        }`}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <MoreVertical size={toolbarTrigger ? 18 : 16} aria-hidden />
      </Button>
      {portal ? (menu && createPortal(menu, document.body)) : menu}
    </div>
  );
}
